import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Link, useSearchParams } from "react-router-dom";
import MovieCard from "@/components/base/MovieCard";
import NewsCard from "@/components/base/NewsCard";
import { movies, type Movie } from "@/mocks/movies";
import { newsList, type NewsArticle } from "@/mocks/news";
import { usePageMeta } from "@/hooks/usePageMeta";

type Scope = "all" | "movie" | "tvseries" | "news";

const scopeTabs: { key: Scope; label: string }[] = [
  { key: "all", label: "全部" },
  { key: "movie", label: "电影" },
  { key: "tvseries", label: "电视剧" },
  { key: "news", label: "资讯" },
];

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = (searchParams.get("q") || "").trim();
  const [scope, setScope] = useState<Scope>("all");
  const [input, setInput] = useState(query);

  useEffect(() => {
    setInput(query);
    setScope("all");
  }, [query]);

  usePageMeta(
    query ? `搜索“${query}”结果 - 国产久久久` : "全站搜索 - 国产久久久",
    query
      ? `国产久久久搜索“${query}”的结果页面，覆盖国产电影、国产电视剧与影视资讯内容。`
      : "国产久久久全站搜索，支持查找国产电影、国产电视剧与影视资讯。",
    "搜索,国产影视,国产久久久"
  );

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const q = input.trim();
    if (!q) return;
    setSearchParams({ q });
  };

  const { matchedMovies, matchedNews } = useMemo(() => {
    if (!query) return { matchedMovies: [] as Movie[], matchedNews: [] as NewsArticle[] };
    const lower = query.toLowerCase();
    const matchMovies = movies.filter((m) => {
      const haystack = [
        m.title,
        m.director,
        m.region,
        ...m.genres,
        ...m.cast,
        m.summary,
        m.synopsis,
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(lower);
    });
    const matchNews = newsList.filter((n) => {
      const haystack = [n.title, n.tag, n.summary, ...n.content]
        .join(" ")
        .toLowerCase();
      return haystack.includes(lower);
    });
    return { matchedMovies: matchMovies, matchedNews: matchNews };
  }, [query]);

  const showMovies = scope === "all" || scope === "movie" || scope === "tvseries";
  const showNews = scope === "all" || scope === "news";

  const visibleMovies = matchedMovies.filter((m) => {
    if (scope === "all" || scope === "news") return true;
    return m.type === scope;
  });

  const movieCount = matchedMovies.length;
  const newsCount = matchedNews.length;
  const total = (showMovies ? visibleMovies.length : 0) + (showNews ? matchedNews.length : 0);

  return (
    <div className="w-full">
      <div className="bg-background-100 border-b border-background-200">
        <div className="w-full px-4 md:px-8 py-6 md:py-8">
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950">
            全站搜索
          </h1>
          <form
            onSubmit={submit}
            className="mt-5 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full max-w-2xl"
          >
            <div className="flex items-center gap-2 flex-1 h-11 rounded-md border border-background-300 bg-background-50 px-3 focus-within:border-primary-400 transition-colors">
              <span className="w-5 h-5 flex items-center justify-center text-foreground-400">
                <i className="ri-search-line text-base"></i>
              </span>
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="输入影片、电视剧或资讯关键词"
                className="w-full bg-transparent text-sm text-foreground-800 placeholder:text-foreground-400 outline-none"
              />
            </div>
            <button
              type="submit"
              className="h-11 px-6 rounded-md bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
            >
              搜索
            </button>
          </form>
        </div>
      </div>

      <div className="w-full px-4 md:px-8 py-8 md:py-10">
        {!query ? (
          <div className="py-20 text-center rounded-lg bg-background-50 border border-background-200">
            <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-5">
              <i className="ri-search-eye-line text-3xl"></i>
            </span>
            <h2 className="font-heading text-lg font-semibold text-foreground-950">
              输入关键词开始搜索
            </h2>
            <p className="mt-2 text-sm text-foreground-500">
              支持搜索国产电影、国产电视剧与影视资讯。
            </p>
          </div>
        ) : (
          <>
            <div className="flex flex-wrap items-center gap-2 mb-6">
              {scopeTabs.map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setScope(tab.key)}
                  className={`px-3 py-1.5 rounded-full text-sm whitespace-nowrap cursor-pointer transition-colors ${
                    scope === tab.key
                      ? "bg-primary-500 text-background-50"
                      : "bg-background-100 text-foreground-600 hover:text-primary-600"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
              <span className="ml-auto text-sm text-foreground-500 whitespace-nowrap">
                共找到 {total} 条结果
              </span>
            </div>

            <p className="text-sm text-foreground-500 mb-6">
              “{query}” 相关结果：影视作品 {movieCount} 部 · 资讯 {newsCount} 篇
            </p>

            {total === 0 ? (
              <div className="py-20 text-center rounded-lg bg-background-50 border border-background-200">
                <span className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-5">
                  <i className="ri-emotion-sad-line text-3xl"></i>
                </span>
                <h2 className="font-heading text-lg font-semibold text-foreground-950">
                  未找到“{query}”相关内容
                </h2>
                <p className="mt-2 text-sm text-foreground-500">
                  换个关键词试试，或者回到首页浏览推荐内容。
                </p>
                <Link
                  to="/"
                  className="inline-block mt-6 px-5 py-2.5 rounded-md bg-primary-500 text-background-50 text-sm whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
                >
                  返回首页
                </Link>
              </div>
            ) : (
              <div className="flex flex-col gap-10">
                {showMovies && visibleMovies.length > 0 && (
                  <section>
                    <div className="flex items-center gap-3 mb-5">
                      <span className="w-1 h-5 rounded-full bg-primary-500"></span>
                      <h2 className="font-heading text-lg font-semibold text-foreground-950">
                        影视作品
                      </h2>
                      <span className="text-sm text-foreground-400">
                        {visibleMovies.length}
                      </span>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
                      {visibleMovies.map((movie) => (
                        <MovieCard key={movie.id} movie={movie} />
                      ))}
                    </div>
                  </section>
                )}

                {showNews && matchedNews.length > 0 && (
                  <section>
                    <div className="flex items-center gap-3 mb-5">
                      <span className="w-1 h-5 rounded-full bg-accent-500"></span>
                      <h2 className="font-heading text-lg font-semibold text-foreground-950">
                        影视资讯
                      </h2>
                      <span className="text-sm text-foreground-400">
                        {matchedNews.length}
                      </span>
                    </div>
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5">
                      {matchedNews.map((article) => (
                        <NewsCard key={article.id} article={article} layout="list" />
                      ))}
                    </div>
                  </section>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}